import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { School } from 'models/school';
import { Photo } from 'models/photo';
import { getSchoolAlbum, getSchoolAlbums, getSchoolPhotos } from 'services/school';
import Loader from 'components/loader';
import { KIND_SERVER_TO_CLIENT_MAPPING } from 'consts/school';
import './Photos.scss';

interface Props {
   school: School;
}

interface PublicAlbum {
   id: string;
   name?: string;
   description?: string;
   coverUrl?: string;
   coverPhotoId?: string;
   academicYear?: string;
   category?: 'SPORT_TEAM' | 'EVENT_ACTION' | 'SCHOOL_LIFE' | 'GENERAL';
   photos?: Photo[];
   createdAt?: string;
   updatedAt?: string;
}

function formatPhotoDate(value?: string) {
   if (!value || Number.isNaN(new Date(value).getTime())) return '';
   return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(value));
}

const SchoolPhotos: React.FC<Props> = ({ school }) => {
   const isUnion = school.kind === KIND_SERVER_TO_CLIENT_MAPPING.SCHOOL_UNION;
   const [searchParams, setSearchParams] = useSearchParams();
   const [albums, setAlbums] = useState<PublicAlbum[]>([]);
   const [album, setAlbum] = useState<PublicAlbum>();
   const [photos, setPhotos] = useState<Photo[]>([]);
   const [isLoading, setIsLoading] = useState(true);
   const [loadError, setLoadError] = useState(false);
   const [yearFilter, setYearFilter] = useState('ALL');
   const [categoryFilter, setCategoryFilter] = useState('ALL');
   const photoId = searchParams.get('photo') || '';
   const albumId = searchParams.get('album') || '';

   useEffect(() => {
      let isActive = true;
      if (!albumId) {
         setAlbum(undefined);
         setPhotos([]);
         return () => { isActive = false; };
      }

      setIsLoading(true);
      setLoadError(false);
      Promise.all([
         getSchoolAlbum(school.id, albumId),
         getSchoolPhotos(school, albumId),
      ])
         .then(([albumData, photoData]: [PublicAlbum, Photo[]]) => {
            if (!isActive) return;
            setAlbum(albumData);
            setPhotos((Array.isArray(photoData) ? photoData : []).filter(photo => photo.accessPreset === 'PUBLIC' && photo.picUrl));
         })
         .catch(() => {
            if (isActive) setLoadError(true);
         })
         .finally(() => {
            if (isActive) setIsLoading(false);
         });

      return () => { isActive = false; };
   }, [school.id, albumId]);

   useEffect(() => {
      let isActive = true;
      setIsLoading(true);
      setLoadError(false);
      getSchoolAlbums(school.id)
         .then((items: PublicAlbum[]) => {
            if (isActive) {
               setAlbums((Array.isArray(items) ? items : []).filter(item => item.id !== school.defaultAlbumId));
            }
         })
         .catch(() => { if (isActive) setLoadError(true); })
         .finally(() => { if (isActive) setIsLoading(false); });
      return () => { isActive = false; };
   }, [school.id, school.defaultAlbumId]);

   const selectedIndex = useMemo(() => photos.findIndex(photo => photo.id === photoId), [photoId, photos]);
   const selectedPhoto = selectedIndex >= 0 ? photos[selectedIndex] : undefined;
   const albumName = album?.name?.trim() || `${school.name} photos`;
   const onOpenPhoto = (id: string) => setSearchParams({ album: albumId, photo: id });
   const onClosePhoto = () => setSearchParams({ album: albumId });
   const onMove = (direction: -1 | 1) => {
      if (photos.length < 2) return;
      const nextIndex = (selectedIndex + direction + photos.length) % photos.length;
      onOpenPhoto(photos[nextIndex].id);
   };

   useEffect(() => {
      if (photoId && photos.length > 0 && selectedIndex < 0) onClosePhoto();
   }, [photoId, photos, selectedIndex]);

   useEffect(() => {
      if (!selectedPhoto) return;
      const onKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') onClosePhoto();
         if (event.key === 'ArrowLeft') onMove(-1);
         if (event.key === 'ArrowRight') onMove(1);
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   }, [selectedPhoto, selectedIndex, photos]);

   if (isLoading) return <div className='school-photos__loading'><Loader /></div>;

   if (!albumId) {
      const categoryLabels: Record<string, string> = { SPORT_TEAM: 'Sport & teams', EVENT_ACTION: 'Events & action', SCHOOL_LIFE: 'School life', GENERAL: 'General' };
      const years = Array.from(new Set(albums.map(item => item.academicYear || 'Archive')));
      const visibleAlbums = albums.filter(item =>
         (yearFilter === 'ALL' || (item.academicYear || 'Archive') === yearFilter) &&
         (categoryFilter === 'ALL' || (item.category || 'GENERAL') === categoryFilter)
      );
      const visibleYears = years.filter(year => visibleAlbums.some(item => (item.academicYear || 'Archive') === year));

      return <main className='school-photos'>
         <header className='school-photos__heading'><div><span>{isUnion ? 'Union gallery' : 'School gallery'}</span><h1>Photos</h1><p>{isUnion ? 'Moments from tournaments, competitions and member events.' : 'Moments from fixtures, competitions and school life.'}</p></div><strong>{albums.length} {albums.length === 1 ? 'album' : 'albums'}</strong></header>
         {albums.length > 0 && <div className='school-photos__filters'><label>Academic year<select value={yearFilter} onChange={event => setYearFilter(event.target.value)}><option value='ALL'>All years</option>{years.map(year => <option value={year} key={year}>{year}</option>)}</select></label><label>Collection<select value={categoryFilter} onChange={event => setCategoryFilter(event.target.value)}><option value='ALL'>All collections</option>{Object.keys(categoryLabels).map(category => <option value={category} key={category}>{categoryLabels[category]}</option>)}</select></label></div>}
         {loadError ? <section className='school-photos__empty' role='alert'><PhotoCameraRoundedIcon /><h2>Photos could not be loaded</h2><p>Please refresh the page and try again.</p></section> : albums.length === 0 ? <section className='school-photos__empty'><PhotoCameraRoundedIcon /><h2>No albums published yet</h2><p>Public albums will appear here after {isUnion ? 'the union' : 'the school'} publishes them.</p></section> : visibleAlbums.length === 0 ? <section className='school-photos__empty'><PhotoCameraRoundedIcon /><h2>No albums match these filters</h2><p>Choose another year or collection.</p></section> : visibleYears.map(year => <section className='school-photos__collection' key={year}>
            <div className='school-photos__collection-heading'><h2>{year}</h2><span>{visibleAlbums.filter(item => (item.academicYear || 'Archive') === year).length} albums</span></div>
            {Object.keys(categoryLabels).map(category => {
               const categoryAlbums = visibleAlbums.filter(item => (item.academicYear || 'Archive') === year && (item.category || 'GENERAL') === category);
               if (categoryAlbums.length === 0) return null;
               return <div className='school-photos__category' key={category}><h3>{categoryLabels[category]}</h3><div className='school-photos__album-grid'>{categoryAlbums.map(item => {
               const cover = item.coverUrl || item.photos?.find(photo => photo.id === item.coverPhotoId)?.picUrl || item.photos?.[0]?.picUrl;
               const count = item.photos?.length || 0;
               return <button type='button' className='school-photos__album-card' key={item.id} onClick={() => setSearchParams({ album: item.id })}>
                  <span className='school-photos__album-cover'>{cover ? <img src={cover} alt='' loading='lazy' /> : <PhotoCameraRoundedIcon />}</span>
                  <span className='school-photos__album-copy'><small>{categoryLabels[item.category || 'GENERAL']}</small><strong>{item.name || `${school.name} photos`}</strong>{item.description && <span>{item.description}</span>}<em>{count} {count === 1 ? 'photo' : 'photos'}</em></span>
               </button>;
               })}</div></div>;
            })}
         </section>)}
      </main>;
   }

   if (selectedPhoto) {
      const caption = selectedPhoto.description?.trim() || (isUnion ? 'Union photo' : 'School photo');
      const takenAt = formatPhotoDate(selectedPhoto.uploadedAt);

      return (
         <main className='school-photos school-photos--detail'>
            <button type='button' className='school-photos__back' onClick={() => selectedPhoto ? onClosePhoto() : setSearchParams({})}>
               <ArrowBackRoundedIcon aria-hidden='true' /> {albumName}
            </button>
            <div className='school-photos__photo-layout'>
               <section className='school-photos__viewer' aria-label={`${caption}, photo ${selectedIndex + 1} of ${photos.length}`}>
                  <img src={selectedPhoto.picUrl} alt={caption} />
                  {photos.length > 1 && (
                     <>
                        <button type='button' className='is-previous' aria-label='Previous photo' onClick={() => onMove(-1)}>
                           <ChevronLeftRoundedIcon />
                        </button>
                        <button type='button' className='is-next' aria-label='Next photo' onClick={() => onMove(1)}>
                           <ChevronRightRoundedIcon />
                        </button>
                     </>
                  )}
               </section>
               <aside className='school-photos__photo-info'>
                  <section>
                     <span>Photo {selectedIndex + 1} of {photos.length}</span>
                     <h1>{caption}</h1>
                     <dl>
                        <div><dt>Album</dt><dd>{albumName}</dd></div>
                        {takenAt && <div><dt>Published</dt><dd>{takenAt}</dd></div>}
                     </dl>
                     <div className='school-photos__photo-actions'>
                        <a href={selectedPhoto.picUrl} target='_blank' rel='noreferrer'>
                           <DownloadRoundedIcon aria-hidden='true' /> Download
                        </a>
                        {selectedPhoto.isOnSale && selectedPhoto.saleLink && (
                           <a className='is-secondary' href={selectedPhoto.saleLink} target='_blank' rel='noreferrer'>Order a print</a>
                        )}
                     </div>
                  </section>
                  <section className='school-photos__usage'>
                     <h2>Using this photo</h2>
                     <p>Photos here have been approved for public use. To ask for a photo to be removed, contact {isUnion ? 'the union' : 'the school office'}.</p>
                  </section>
               </aside>
            </div>
            {photos.length > 1 && (
               <div className='school-photos__strip' aria-label='Other photos in this album'>
                  {photos.map(photo => (
                     <button
                        key={photo.id}
                        type='button'
                        className={photo.id === selectedPhoto.id ? 'is-active' : ''}
                        onClick={() => onOpenPhoto(photo.id)}
                        aria-label={`Open ${photo.description?.trim() || 'photo'}`}
                     >
                        <img src={photo.picUrl} alt='' loading='lazy' />
                     </button>
                  ))}
               </div>
            )}
         </main>
      );
   }

   return (
      <main className='school-photos'>
         <button type='button' className='school-photos__back' onClick={() => setSearchParams({})}><ArrowBackRoundedIcon /> All albums</button>
         <header className='school-photos__heading'>
            <div>
               <span>{isUnion ? 'Union gallery' : 'School gallery'}</span>
               <h1>Photos</h1>
               <p>{isUnion ? 'Moments from tournaments, competitions and member events.' : 'Moments from fixtures, competitions and school sport.'}</p>
            </div>
            {photos.length > 0 && <strong>{photos.length} {photos.length === 1 ? 'photo' : 'photos'}</strong>}
         </header>

         {loadError ? (
            <section className='school-photos__empty' role='alert'>
               <PhotoCameraRoundedIcon aria-hidden='true' />
               <h2>Photos could not be loaded</h2>
               <p>Please refresh the page and try again.</p>
            </section>
         ) : photos.length === 0 ? (
            <section className='school-photos__empty'>
               <PhotoCameraRoundedIcon aria-hidden='true' />
               <h2>No photos published yet</h2>
               <p>Public photos will appear here after {isUnion ? 'the union' : 'the school'} has checked consent and published them.</p>
               <Link to={isUnion ? '/tournamentResults' : '/results'}>See recent results <ArrowForwardRoundedIcon aria-hidden='true' /></Link>
            </section>
         ) : (
            <>
               {photos.some(photo => photo.isOnSale && photo.saleLink) && (
                  <aside className='school-photos__sale'>
                     <ShoppingBagOutlinedIcon aria-hidden='true' />
                     <div><strong>Prints are available for selected photos</strong><span>Open a marked photo to visit the photographer’s order page.</span></div>
                  </aside>
               )}
               <section className='school-photos__album'>
                  <div className='school-photos__album-heading'>
                     <div><h2>{albumName}</h2>{album?.description && <p>{album.description}</p>}</div>
                     <span>{photos.length} {photos.length === 1 ? 'photo' : 'photos'}</span>
                  </div>
                  <div className='school-photos__grid'>
                     {photos.map(photo => {
                        const caption = photo.description?.trim() || (isUnion ? 'Union photo' : 'School photo');
                        return (
                           <button type='button' key={photo.id} onClick={() => onOpenPhoto(photo.id)} aria-label={`Open ${caption}`}>
                              <img src={photo.picUrl} alt={caption} loading='lazy' />
                              {photo.isOnSale && photo.saleLink && <span><ShoppingBagOutlinedIcon /> Prints</span>}
                           </button>
                        );
                     })}
                  </div>
               </section>
               <p className='school-photos__privacy-note'>Only photos approved for public access appear here. Private photos remain inside the Squad In Touch app.</p>
            </>
         )}
      </main>
   );
};

export default SchoolPhotos;
